/**
 * CONFIRM BEFORE AN ESTIMATE GOES TO A CLIENT — every estimate send path shows
 * this first (the lead file's Email estimate, and a re-send from Send history).
 *
 * The figures come from the server (GET …/email-estimate/check, the same
 * breakdown the send and the portal use), not from whatever is on screen.
 * Warnings are amber and never block; a refusal the send route would make
 * anyway (no email, a $0 estimate) is red and disables Send.
 */

import React, { useEffect, useState } from 'react';
import { useApiClient } from '../hooks/useApi';
import { formatMoney } from '../lib/utils';

type Check = { clientName: string; email: string; total: number; lineCount: number; warnings: string[]; blocked: string | null };

export function EstimateSendConfirm({ leadId, onConfirm, onCancel }: { leadId: string; onConfirm: () => void; onCancel: () => void }) {
  const api = useApiClient();
  const [check, setCheck] = useState<Check | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.checkEstimateSend(leadId)
      .then((r) => { if (alive) setCheck(r.data ?? null); })
      .catch((e: any) => { if (alive) setError(e?.message || 'Could not check the estimate.'); });
    return () => { alive = false; };
  }, [api, leadId]);

  const btn = (primary: boolean, disabled = false): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
    background: primary ? (disabled ? 'var(--crm-line)' : 'var(--crm-accent-fill)') : 'var(--crm-panel-2)', color: primary ? 'var(--crm-on-fill)' : 'var(--crm-muted)',
    border: primary ? 'none' : '1px solid var(--crm-line)', opacity: disabled ? 0.7 : 1,
  });

  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div
        role="dialog" aria-modal="true" aria-label="Send estimate"
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '12px', padding: '20px', width: '420px', maxWidth: '92vw', maxHeight: '90dvh', overflowY: 'auto', boxShadow: '0 20px 60px var(--crm-shadow-color)' }}
      >
        {!check && !error && <div style={{ fontSize: '13px', color: 'var(--crm-muted)' }}>Checking the estimate…</div>}
        {error && <div role="alert" style={{ fontSize: '13px', color: 'var(--crm-red)' }}>{error}</div>}
        {check && (
          <>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--crm-text)', marginBottom: '10px', lineHeight: 1.4 }}>
              Send estimate to {check.clientName || 'this client'} <span style={{ color: 'var(--crm-muted)', fontWeight: 500 }}>{check.email || '(no email)'}</span>?
            </div>
            <div style={{ fontSize: '13px', color: 'var(--crm-text)', marginBottom: '12px' }}>
              Total <strong style={{ fontFamily: "'DM Mono', monospace" }}>{formatMoney(check.total)}</strong>
              <span style={{ color: 'var(--crm-muted)' }}> · {check.lineCount} line{check.lineCount === 1 ? '' : 's'}</span>
            </div>
            {check.warnings.length > 0 && (
              <ul style={{ margin: '0 0 12px', padding: '8px 10px 8px 26px', background: 'var(--crm-amber-tint)', border: '1px solid var(--crm-amber-soft-line)', borderRadius: '8px', color: 'var(--crm-amber)', fontSize: '12px', lineHeight: 1.6 }}>
                {check.warnings.map((w) => <li key={w}>{w}</li>)}
              </ul>
            )}
            {check.blocked && (
              <div role="alert" style={{ marginBottom: '12px', fontSize: '12px', color: 'var(--crm-red)' }}>{check.blocked}</div>
            )}
          </>
        )}
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={btn(false)}>Cancel</button>
          <button
            onClick={onConfirm}
            disabled={!check || !!check.blocked}
            style={btn(true, !check || !!check.blocked)}
          >
            {check?.warnings.length ? 'Send anyway' : 'Send estimate'}
          </button>
        </div>
      </div>
    </div>
  );
}
